import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { socialService } from '../../../services/social'
import { SocialAuthor } from '../../../types/social'
import AuthorAvatar from './AuthorAvatar'
import { formatAuthorName } from '../../../utils/userDisplayName'
import {
  ActiveMention,
  getActiveMention,
  insertMentionAt,
} from './mentionUtils'

export interface MentionTextareaProps {
  value: string
  onChange: (value: string) => void
  maxLength: number
  placeholder?: string
  rows?: number
  multiline?: boolean
  disabled?: boolean
  className?: string
  onSubmit?: () => void
  autoFocus?: boolean
}

const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  maxLength,
  placeholder,
  rows = 3,
  multiline = true,
  disabled = false,
  className,
  onSubmit,
  autoFocus = false,
}) => {
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(null)
  const [suggestions, setSuggestions] = useState<SocialAuthor[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(0)
  const searchSeq = useRef(0)
  const [dropdownStyle, setDropdownStyle] = useState<{ top: number; left: number; width: number } | null>(null)

  const updateDropdownPosition = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setDropdownStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    })
  }, [])

  const syncMentionState = useCallback((text: string, cursor: number) => {
    const active = getActiveMention(text, cursor)
    setActiveMention(active)
    setHighlightIndex(0)
    return active
  }, [])

  useEffect(() => {
    if (!activeMention) {
      setSuggestions([])
      return
    }

    const seq = ++searchSeq.current
    const timer = window.setTimeout(async () => {
      setLoadingSuggestions(true)
      try {
        const users = await socialService.searchMentionUsers(activeMention.query)
        if (searchSeq.current === seq) {
          setSuggestions(users)
          setHighlightIndex(0)
        }
      } catch {
        if (searchSeq.current === seq) setSuggestions([])
      } finally {
        if (searchSeq.current === seq) setLoadingSuggestions(false)
      }
    }, 200)

    return () => clearTimeout(timer)
  }, [activeMention])

  const showDropdown = !!activeMention

  useEffect(() => {
    if (!showDropdown) {
      setDropdownStyle(null)
      return
    }

    updateDropdownPosition()
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  }, [showDropdown, updateDropdownPosition, activeMention?.query, suggestions.length])

  const applyMention = (user: SocialAuthor) => {
    if (!activeMention) return
    const el = inputRef.current
    const cursor = el?.selectionStart ?? value.length
    const currentMention = getActiveMention(value, cursor) ?? activeMention
    const { text, cursor: nextCursor } = insertMentionAt(value, currentMention, user)
    onChange(text.slice(0, maxLength))
    setActiveMention(null)
    setSuggestions([])

    requestAnimationFrame(() => {
      const target = inputRef.current
      if (!target) return
      target.focus()
      const pos = Math.min(nextCursor, maxLength)
      target.setSelectionRange(pos, pos)
    })
  }

  const handleChange = (next: string) => {
    onChange(next.slice(0, maxLength))
    const el = inputRef.current
    const cursor = el?.selectionStart ?? next.length
    syncMentionState(next, cursor)
  }

  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const el = e.currentTarget
    syncMentionState(value, el.selectionStart ?? value.length)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const dropdownOpen = activeMention && (suggestions.length > 0 || loadingSuggestions)

    if (dropdownOpen && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightIndex((i) => (i + 1) % suggestions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightIndex((i) => (i - 1 + suggestions.length) % suggestions.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        applyMention(suggestions[highlightIndex])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setActiveMention(null)
        setSuggestions([])
        return
      }
    }

    if (e.key === 'Enter' && !multiline && onSubmit) {
      e.preventDefault()
      onSubmit()
    }
  }

  const inputClassName = clsx(
    'w-full rounded-lg border border-gray-200 dark:border-gray-600',
    'bg-white dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100',
    'placeholder:text-gray-400 dark:placeholder:text-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500/30',
    multiline ? 'resize-none' : '',
    className
  )

  const dropdownContent = showDropdown && dropdownStyle ? (
    <div
      style={{
        position: 'fixed',
        top: dropdownStyle.top,
        left: dropdownStyle.left,
        width: dropdownStyle.width,
        zIndex: 200,
      }}
      className={clsx(
        'max-h-48 overflow-y-auto',
        'rounded-lg border border-gray-200 dark:border-gray-600',
        'bg-white dark:bg-gray-800 shadow-lg'
      )}
    >
      {loadingSuggestions && (
        <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
          Buscando usuários...
        </div>
      )}
      {!loadingSuggestions && suggestions.length === 0 && (
        <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
          {activeMention?.query ? 'Nenhum usuário encontrado' : 'Digite o nome do usuário'}
        </div>
      )}
      {suggestions.map((user, index) => (
        <button
          key={user.id}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            applyMention(user)
          }}
          className={clsx(
            'w-full flex items-center gap-2 px-3 py-2 text-left text-sm',
            'hover:bg-gray-50 dark:hover:bg-gray-700/60',
            index === highlightIndex && 'bg-primary-50 dark:bg-primary-900/30'
          )}
        >
          <AuthorAvatar name={user.name} avatarPath={user.avatar_url} size="sm" />
          <span className="truncate text-gray-900 dark:text-gray-100">
            {formatAuthorName(user)}
          </span>
        </button>
      ))}
    </div>
  ) : null

  return (
    <div className="relative">
      {multiline ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          autoFocus={autoFocus}
          className={inputClassName}
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          autoFocus={autoFocus}
          className={inputClassName}
        />
      )}

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  )
}

export default MentionTextarea
